import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';

import { API } from "../config";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#F9F6F0] relative overflow-hidden">
      {/* Background drifting blobs */}
      <div className="bg-blob blob-gold top-[10%] left-[-5%] w-[400px] h-[400px] opacity-35" />
      <div className="bg-blob blob-saffron bottom-[15%] right-[-5%] w-[450px] h-[450px] opacity-40" />

      <div className="container mx-auto max-w-5xl px-6 md:px-12 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[10px] font-bold tracking-widest uppercase text-[#D97706] mb-2 block">
            Get In Touch
          </span>
          <h1 className="text-3xl md:text-5xl font-bold font-vedic text-[#352520] mb-4">
            Connect with Our Team
          </h1>
          <p className="text-sm md:text-base text-[#5D4E37] max-w-xl mx-auto font-light leading-relaxed">
            Have questions about our AI floor plan analyzer or premium Vastu reports? Send us a message.
          </p>
        </div>

        {/* 3 Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 text-left">
          {/* Email */}
          <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-3xl hover:shadow-md transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-[#FFF3E0] flex items-center justify-center text-[#D97706] mb-4">
              <Mail className="w-5 h-5" />
            </div>
            <h3 className="font-bold font-vedic text-[#352520] mb-1">Email Us</h3>
            <p className="text-xs text-[#5D4E37] font-light">support@vastuai.com</p>
          </div>

          {/* Call */}
          <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-3xl hover:shadow-md transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-[#FFF3E0] flex items-center justify-center text-[#D97706] mb-4">
              <Phone className="w-5 h-5" />
            </div>
            <h3 className="font-bold font-vedic text-[#352520] mb-1">Call Us</h3>
            <p className="text-xs text-[#5D4E37] font-light">+91 98765 43210</p>
          </div>

          {/* Address */}
          <div className="bg-white/60 backdrop-blur-md border border-[#EBE6DD]/60 p-6 rounded-3xl hover:shadow-md transition-all duration-300">
            <div className="w-10 h-10 rounded-xl bg-[#FFF3E0] flex items-center justify-center text-[#D97706] mb-4">
              <MapPin className="w-5 h-5" />
            </div>
            <h3 className="font-bold font-vedic text-[#352520] mb-1">Visit Us</h3>
            <p className="text-xs text-[#5D4E37] font-light">Mumbai, India</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white border border-[#EBE6DD]/60 rounded-3xl p-8 md:p-12 shadow-lg text-left max-w-3xl mx-auto">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                <Send className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold font-vedic text-[#352520] mb-2">Message Sent!</h3>
              <p className="text-sm text-[#5D4E37] font-light mb-6">
                Thank you for reaching out. We will get back to you within 24 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="border-2 border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-[#352520] mb-2 uppercase tracking-wide">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#352520] mb-2 uppercase tracking-wide">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-[#352520] mb-2 uppercase tracking-wide">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706]"
                  placeholder="How can we help you?"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-[#352520] mb-2 uppercase tracking-wide">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 bg-[#F9F6F0]/40 border border-[#EBE6DD] rounded-xl text-sm font-medium text-[#352520] placeholder-[#826239]/60 focus:outline-none focus:border-[#D97706] resize-none"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#D97706] hover:bg-[#C25E03] text-white py-4 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md active:scale-97 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
